import { useState, FormEvent, useEffect } from 'react'
import { router, useForm } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { CheckIcon, ChevronsUpDownIcon, LoaderIcon, CopyIcon, CheckCircleIcon, MailIcon } from 'lucide-react'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import { Button } from '../../../components/ui/button'
import { Checkbox } from '../../../components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover'
import { cn } from '../../../lib/utils'

interface Organization {
  id: number
  name: string
}

interface CreatedUser {
  id: number
  email: string
  first_name: string
  last_name: string
  organization_name: string
  generated_password: string
  email_sent: boolean
}

interface AdminUsersNewProps {
  organizations: Organization[]
  created_user?: CreatedUser
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function New({ organizations: initialOrganizations, created_user }: AdminUsersNewProps) {
  const { t } = useTranslation('admin')
  const [orgType, setOrgType] = useState<'existing' | 'new'>('existing')
  const [orgPopoverOpen, setOrgPopoverOpen] = useState(false)
  const [orgSearch, setOrgSearch] = useState('')
  const [organizations, setOrganizations] = useState<Organization[]>(initialOrganizations)
  const [isSearching, setIsSearching] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null)
  const [passwordCopied, setPasswordCopied] = useState(false)

  const { data, setData, post, processing, errors, reset } = useForm({
    first_name: '',
    last_name: '',
    email: '',
    organization_id: '',
    new_organization_name: '',
    organization_role: 'member',
    send_password_email: false as boolean
  })

  // Debounced search for organizations
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (orgSearch.length === 0) {
        setOrganizations(initialOrganizations)
        return
      }

      setIsSearching(true)
      try {
        const response = await fetch(`/admin/organizations/search?q=${encodeURIComponent(orgSearch)}`)
        const results = await response.json()
        setOrganizations(results)
      } catch (error) {
        console.error('Search failed:', error)
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [orgSearch, initialOrganizations])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post('/admin/users')
  }

  const handleCopyPassword = async () => {
    if (created_user?.generated_password) {
      await navigator.clipboard.writeText(created_user.generated_password)
      setPasswordCopied(true)
      setTimeout(() => setPasswordCopied(false), 2000)
    }
  }

  const handleCreateAnother = () => {
    reset()
    setSelectedOrg(null)
    setOrgType('existing')
    router.visit('/admin/users/new')
  }

  // Show success state if user was just created
  if (created_user) {
    return (
      <>
        <AppHeader
          breadcrumbs={[
            { label: 'Admin', href: '/admin' },
            { label: t('users.index.title'), href: '/admin/users' },
            { label: t('users.new.title') }
          ]}
        />

        <div className="flex flex-1 flex-col">
          <div className="@container/main flex flex-1 flex-col gap-2">
            <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
              <div className="px-4 lg:px-6 max-w-2xl">
                <Card className="shadow-none p-6">
                  <div className="text-center mb-6">
                    <div className="mx-auto w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mb-4">
                      <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground">
                      {t('users.new.success.title')}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('users.new.success.description', { email: created_user.email })}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-muted rounded-lg p-4 space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t('users.new.success.name')}</span>
                        <span className="font-medium">{created_user.first_name} {created_user.last_name}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t('users.new.success.email')}</span>
                        <span className="font-medium">{created_user.email}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{t('users.new.success.organization')}</span>
                        <span className="font-medium">{created_user.organization_name}</span>
                      </div>
                    </div>

                    <div className="border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-2">
                            {t('users.new.success.passwordLabel')}
                          </p>
                          <div className="flex items-center gap-2">
                            <code className="flex-1 bg-white dark:bg-gray-900 px-3 py-2 rounded border text-sm font-mono select-all">
                              {created_user.generated_password}
                            </code>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCopyPassword}
                              className="shrink-0"
                            >
                              {passwordCopied ? (
                                <CheckIcon className="w-4 h-4 text-green-600" />
                              ) : (
                                <CopyIcon className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                          <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                            {t('users.new.success.passwordNote')}
                          </p>
                        </div>
                      </div>
                    </div>

                    {created_user.email_sent && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted rounded-lg p-3">
                        <MailIcon className="w-4 h-4" />
                        <span>{t('users.new.success.emailSent')}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 mt-6 pt-4 border-t">
                    <Button onClick={handleCreateAnother}>
                      {t('users.new.success.createAnother')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => router.visit('/admin/users')}
                    >
                      {t('users.new.success.backToUsers')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => router.visit(`/admin/users/${created_user.id}`)}
                    >
                      {t('users.new.success.viewUser')}
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <AppHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: t('users.index.title'), href: '/admin/users' },
          { label: t('users.new.title') }
        ]}
      />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6 max-w-2xl">
              {/* Page Header */}
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-foreground">{t('users.new.title')}</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  {t('users.new.description')}
                </p>
              </div>

              {/* Form */}
              <Card className="shadow-none p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">{t('users.new.form.firstName')}</Label>
                      <Input
                        id="first_name"
                        type="text"
                        placeholder={t('users.new.form.firstNamePlaceholder')}
                        value={data.first_name}
                        onChange={(e) => setData('first_name', e.target.value)}
                        required
                      />
                      {errors.first_name && (
                        <p className="text-sm text-red-600 dark:text-red-400">{errors.first_name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">{t('users.new.form.lastName')}</Label>
                      <Input
                        id="last_name"
                        type="text"
                        placeholder={t('users.new.form.lastNamePlaceholder')}
                        value={data.last_name}
                        onChange={(e) => setData('last_name', e.target.value)}
                        required
                      />
                      {errors.last_name && (
                        <p className="text-sm text-red-600 dark:text-red-400">{errors.last_name}</p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('users.new.form.email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder={t('users.new.form.emailPlaceholder')}
                      value={data.email}
                      onChange={(e) => setData('email', e.target.value)}
                      required
                    />
                    {errors.email && (
                      <p className="text-sm text-red-600 dark:text-red-400">{errors.email}</p>
                    )}
                  </div>

                  {/* Organization Type */}
                  <div className="space-y-4">
                    <Label>{t('users.new.form.organizationLabel')}</Label>
                    <RadioGroup
                      value={orgType}
                      onValueChange={(value: 'existing' | 'new') => {
                        setOrgType(value)
                        if (value === 'existing') {
                          setData('new_organization_name', '')
                        } else {
                          setData('organization_id', '')
                          setSelectedOrg(null)
                        }
                      }}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="existing" id="org-existing" />
                        <Label htmlFor="org-existing" className="cursor-pointer font-normal">
                          {t('users.new.form.existingOrg')}
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="new" id="org-new" />
                        <Label htmlFor="org-new" className="cursor-pointer font-normal">
                          {t('users.new.form.newOrg')}
                        </Label>
                      </div>
                    </RadioGroup>

                    {/* Existing Organization Dropdown */}
                    {orgType === 'existing' && (
                      <div className="space-y-2">
                        <Popover open={orgPopoverOpen} onOpenChange={setOrgPopoverOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={orgPopoverOpen}
                              className="w-full justify-between font-normal"
                            >
                              {selectedOrg?.name || t('users.new.form.selectOrganization')}
                              <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                            <Command shouldFilter={false}>
                              <CommandInput
                                placeholder={t('users.new.form.searchOrganization')}
                                value={orgSearch}
                                onValueChange={setOrgSearch}
                              />
                              <CommandList>
                                {isSearching ? (
                                  <div className="py-6 text-center text-sm">
                                    <LoaderIcon className="mx-auto h-4 w-4 animate-spin" />
                                  </div>
                                ) : organizations.length === 0 ? (
                                  <CommandEmpty>{t('users.new.form.noOrganizations')}</CommandEmpty>
                                ) : (
                                  <CommandGroup>
                                    {organizations.map((org) => (
                                      <CommandItem
                                        key={org.id}
                                        value={org.name}
                                        onSelect={() => {
                                          setData('organization_id', org.id.toString())
                                          setSelectedOrg(org)
                                          setOrgPopoverOpen(false)
                                          setOrgSearch('')
                                        }}
                                      >
                                        {org.name}
                                        <CheckIcon
                                          className={cn(
                                            "ml-auto h-4 w-4",
                                            data.organization_id === org.id.toString() ? "opacity-100" : "opacity-0"
                                          )}
                                        />
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                )}
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        {errors.organization_id && (
                          <p className="text-sm text-red-600 dark:text-red-400">{errors.organization_id}</p>
                        )}
                      </div>
                    )}

                    {/* New Organization Name */}
                    {orgType === 'new' && (
                      <div className="space-y-2">
                        <Input
                          id="new_organization_name"
                          type="text"
                          placeholder={t('users.new.form.newOrgPlaceholder')}
                          value={data.new_organization_name}
                          onChange={(e) => setData('new_organization_name', e.target.value)}
                        />
                        {errors.new_organization_name && (
                          <p className="text-sm text-red-600 dark:text-red-400">{errors.new_organization_name}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Organization Role */}
                  <div className="space-y-2">
                    <Label htmlFor="organization_role">{t('users.new.form.role')}</Label>
                    <Select
                      value={data.organization_role}
                      onValueChange={(value) => setData('organization_role', value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t('users.new.form.selectRole')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">{t('users.new.form.roleMember')}</SelectItem>
                        <SelectItem value="admin">{t('users.new.form.roleAdmin')}</SelectItem>
                        <SelectItem value="external">{t('users.new.form.roleExternal')}</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.organization_role && (
                      <p className="text-sm text-red-600 dark:text-red-400">{errors.organization_role}</p>
                    )}
                  </div>

                  {/* Send Password Email */}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="send_password_email"
                      checked={data.send_password_email}
                      onCheckedChange={(checked) => setData('send_password_email', checked === true)}
                    />
                    <Label htmlFor="send_password_email" className="cursor-pointer font-normal">
                      {t('users.new.form.sendPasswordEmail')}
                    </Label>
                  </div>

                  {/* Form Actions */}
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" disabled={processing}>
                      {processing ? t('users.new.buttons.creating') : t('users.new.buttons.create')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => router.visit('/admin/users')}
                      disabled={processing}
                    >
                      {t('users.new.buttons.cancel')}
                    </Button>
                  </div>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

New.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default New
